import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";

import { FaCrown, FaExclamationTriangle, FaGift, FaNotesMedical, FaTimes } from "react-icons/fa";

import useCommon from "hooks/useCommon";
import { getGuestFlags } from "store/slice/guestSlice";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import { setGuestFlag } from "store/slice/guestSlice";
import { resetFlagUpdateState } from "store/slice/guestSlice";
import OverlayLoader from "components/OverlayLoader";
import { CircularProgress } from "@mui/material";

const ICON_MAP = {
  crown: FaCrown,
  exclamation_triangle: FaExclamationTriangle,
  gift: FaGift,
  medical: FaNotesMedical,
};

const { black, dark, primary, success, light } = colors;
const { borderWidth } = borders;

const ManageGuestFlagModal = ({ open, guest, onClose, onSave }) => {
  const { DISPATCH, STORE } = useCommon();

  const { flags, flagsLoading, flagUpdating, flagUpdateError, flagUpdateSuccess } = STORE(
    (state) => state.guests
  );

  const [selectedFlag, setSelectedFlag] = useState(null);
  const [reason, setReason] = useState("");

  const handleSave = () => {
    if (!selectedFlag) return;

    DISPATCH(
      setGuestFlag({
        hotelId: guest.hotelId,
        guestId: guest.id,
        guestFlag: selectedFlag,
        guestFlagReason: reason,
      })
    );
  };

  const handleClear = () => {
    DISPATCH(
      setGuestFlag({
        hotelId: guest.hotelId,
        guestId: guest.id,
        guestFlag: null,
        guestFlagReason: null,
      })
    );
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") return;
    // setSelectedFlag(null);
    // setReason("");
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    //  Reset Redux state
    DISPATCH(resetFlagUpdateState());

    // Initialize local state
    if (guest?.guestFlag) {
      setSelectedFlag(guest.guestFlag);
      setReason(guest.guestFlagReason || "");
    } else {
      setSelectedFlag(null);
      setReason("");
    }

    //  Fetch flags if needed
    if (!flags.length && guest?.hotelId) {
      DISPATCH(getGuestFlags({ hotelId: guest.hotelId }));
    }
  }, [open, guest?.id]);

  useEffect(() => {
    if (flagUpdateSuccess) {
      DISPATCH(resetFlagUpdateState());
      onClose();
    }
  }, [flagUpdateSuccess]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <VuiBox display="flex" justifyContent="space-between" alignItems="center">
          <VuiTypography variant="lg">Manage Guest Flag</VuiTypography>

          <IconButton size="small" onClick={onClose}>
            <FaTimes color={primary.main} />
          </IconButton>
        </VuiBox>

        {guest && (
          <VuiTypography variant="caption" mt={0.5}>
            {guest.name} · {guest.room}
          </VuiTypography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2, mt: 2 }}>
        <OverlayLoader open={flagUpdating} message="Updating guest flag..." />
        {/* FLAGS GRID */}
        {flagsLoading ? (
          <VuiBox display="flex" justifyContent="center" alignItems="center" minHeight="160px">
            <CircularProgress size={28} sx={{ color: "#1B2E24" }} />
          </VuiBox>
        ) : (
          <VuiBox
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
            }}
            gap={1.5}
            mb={3}
          >
            {flags.map((flag) => {
              if (!flag) return null;

              const isActive = selectedFlag === flag.key;
              const Icon = ICON_MAP[flag.icon];

              return (
                <VuiBox
                  key={flag.key}
                  onClick={() => !flagUpdating && setSelectedFlag(flag.key)}
                  p={1.5}
                  borderRadius="14px"
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    cursor: "pointer",
                    border: `1px solid ${isActive ? `${flag.color}AA` : "rgba(255,255,255,0.12)"}`,
                    background: isActive ? `${flag.color}22` : "rgba(255,255,255,0.03)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${flag.color}18, rgba(255,255,255,0.04))`,
                    },
                  }}
                >
                  <VuiBox>
                    <VuiTypography fontWeight="medium">
                      {Icon && <Icon size={16} color={flag.color} style={{ marginRight: 10 }} />}
                      {flag.label}
                    </VuiTypography>

                    <VuiTypography variant="caption">{flag.description}</VuiTypography>
                  </VuiBox>
                </VuiBox>
              );
            })}
          </VuiBox>
        )}

        {/* REASON */}
        {selectedFlag && (
          <>
            <VuiTypography variant="caption" mb={0.5}>
              Reason (internal)
            </VuiTypography>

            <VuiInput
              gradient
              multiline
              rows={3}
              placeholder="Explain why this flag is assigned…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </>
        )}

        {flagUpdateError && (
          <VuiTypography variant="caption" color="error" mt={1}>
            {flagUpdateError}
          </VuiTypography>
        )}

        {/* ACTIONS */}
        <VuiBox display="flex" justifyContent="space-between" mt={3}>
          <VuiButton
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleClear}
            disabled={!guest?.guestFlag || flagUpdating || flagsLoading}
          >
            Remove Flag
          </VuiButton>

          <VuiButton
            color="primary"
            size="small"
            disabled={!selectedFlag || !reason.trim() || flagUpdating || flagsLoading}
            onClick={handleSave}
          >
            {flagUpdating ? "Saving..." : "Save Flag"}
          </VuiButton>
        </VuiBox>
      </DialogContent>
    </Dialog>
  );
};

export default ManageGuestFlagModal;
